import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent,
} from "react";
import type { PtzState } from "../../api/ptz.ts";
import type { PtzDir } from "../../lib/usePtzContinuousControl.ts";
import { usePtzWheelZoom } from "../../lib/ptzZoomWheel.ts";

/** Drag past center before zoom starts. */
const SPRING_ACTIVATE = 0.1;
/** Release only when nearly centered (hysteresis). */
const SPRING_RELEASE = 0.05;
const SPRING_TRAVEL_PCT = 44;

export type PtzZoomPanelProps = {
  inPlayer?: boolean;
  compact?: boolean;
  wheelZoom?: boolean;
  ptz: PtzState;
  ptzOperable: boolean;
  sliderMin: number;
  sliderMax: number;
  onZoomCommit: (target: number) => void;
  onResetZoom: () => void;
  onZoomPressStart?: (dir: Extract<PtzDir, "zoomIn" | "zoomOut">) => void;
  onZoomPressEnd?: () => void;
  atLimit?: { zoomIn: boolean; zoomOut: boolean };
};

export function PtzZoomPanel({
  inPlayer,
  compact,
  wheelZoom = true,
  ptz,
  ptzOperable,
  sliderMin,
  sliderMax,
  onZoomCommit,
  onResetZoom,
  onZoomPressStart,
  onZoomPressEnd,
  atLimit = { zoomIn: false, zoomOut: false },
}: PtzZoomPanelProps) {
  const displayZoom = Math.min(
    sliderMax,
    Math.max(sliderMin, Math.round(ptz.zoom)),
  );

  const trackRef = useRef<HTMLDivElement | null>(null);
  const activeDirRef = useRef<"zoomIn" | "zoomOut" | null>(null);
  const pointerIdRef = useRef<number | null>(null);
  const onStartRef = useRef(onZoomPressStart);
  const onEndRef = useRef(onZoomPressEnd);
  const atLimitRef = useRef(atLimit);
  onStartRef.current = onZoomPressStart;
  onEndRef.current = onZoomPressEnd;
  atLimitRef.current = atLimit;

  const [thumbNorm, setThumbNorm] = useState(0);
  const [dragging, setDragging] = useState(false);

  const clampTarget = useCallback(
    (value: number) =>
      Math.min(sliderMax, Math.max(sliderMin, Math.round(value))),
    [sliderMin, sliderMax],
  );

  const { attachWheel } = usePtzWheelZoom({
    operable: ptzOperable,
    min: sliderMin,
    max: sliderMax,
    getZoom: () => displayZoom,
    commitZoom: (z) => {
      if (activeDirRef.current) return;
      onZoomCommit(clampTarget(z));
    },
  });

  const panelRowRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!wheelZoom) return undefined;
    const cleanups = [
      attachWheel(trackRef.current),
      attachWheel(panelRowRef.current),
    ];
    return () => {
      for (const off of cleanups) off();
    };
  }, [attachWheel, wheelZoom]);

  const stopZoom = useCallback(() => {
    if (activeDirRef.current !== null) {
      activeDirRef.current = null;
      onEndRef.current?.();
    }
  }, []);

  const engageZoom = useCallback((dir: "zoomIn" | "zoomOut") => {
    if (activeDirRef.current === dir) {
      onStartRef.current?.(dir);
      return;
    }
    if (activeDirRef.current !== null) {
      activeDirRef.current = null;
      onEndRef.current?.();
    }
    activeDirRef.current = dir;
    onStartRef.current?.(dir);
  }, []);

  const releaseZoom = useCallback(() => {
    if (activeDirRef.current === null) return;
    activeDirRef.current = null;
    onEndRef.current?.();
  }, []);

  const applyDirFromNorm = useCallback(
    (norm: number) => {
      const lim = atLimitRef.current;

      if (Math.abs(norm) < SPRING_RELEASE) {
        releaseZoom();
        return;
      }

      if (norm < -SPRING_ACTIVATE) {
        if (!lim.zoomOut) engageZoom("zoomOut");
        return;
      }

      if (norm > SPRING_ACTIVATE) {
        if (!lim.zoomIn) engageZoom("zoomIn");
      }
    },
    [engageZoom, releaseZoom],
  );

  const normFromPointer = useCallback((clientX: number): number => {
    const track = trackRef.current;
    if (!track) return 0;
    const rect = track.getBoundingClientRect();
    if (rect.width <= 0) return 0;
    const x = clientX - rect.left;
    const center = rect.width / 2;
    const half = Math.max(center - 20, 1);
    return Math.max(-1, Math.min(1, (x - center) / half));
  }, []);

  const releaseSpring = useCallback(() => {
    pointerIdRef.current = null;
    setDragging(false);
    setThumbNorm(0);
    stopZoom();
  }, [stopZoom]);

  const onTrackPointerDown = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (!ptzOperable || !onZoomPressStart) return;
      e.preventDefault();
      e.stopPropagation();
      pointerIdRef.current = e.pointerId;
      setDragging(true);
      const norm = normFromPointer(e.clientX);
      setThumbNorm(norm);
      applyDirFromNorm(norm);
    },
    [applyDirFromNorm, normFromPointer, onZoomPressStart, ptzOperable],
  );

  useEffect(() => {
    if (!dragging) return undefined;
    const pid = pointerIdRef.current;
    if (pid === null) return undefined;

    const onMove = (e: globalThis.PointerEvent) => {
      if (e.pointerId !== pid) return;
      e.preventDefault();
      const norm = normFromPointer(e.clientX);
      setThumbNorm(norm);
      applyDirFromNorm(norm);
    };

    const onUp = (e: globalThis.PointerEvent) => {
      if (e.pointerId !== pid) return;
      releaseSpring();
    };

    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [applyDirFromNorm, dragging, normFromPointer, releaseSpring]);

  useEffect(() => {
    return () => {
      if (activeDirRef.current !== null) {
        activeDirRef.current = null;
        onEndRef.current?.();
      }
    };
  }, []);

  const panelClass = [
    "ds-ptz-zoom-panel",
    compact ? "ds-ptz-zoom-panel--compact" : "",
    inPlayer ? "ds-ptz-zoom-panel--in-player" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const thumbPct = 50 + thumbNorm * SPRING_TRAVEL_PCT;
  const springActive =
    thumbNorm < -SPRING_ACTIVATE || thumbNorm > SPRING_ACTIVATE;
  const activeSide =
    thumbNorm > SPRING_ACTIVATE
      ? "in"
      : thumbNorm < -SPRING_ACTIVATE
        ? "out"
        : null;

  return (
    <div className={panelClass}>
      <div ref={panelRowRef} className="ds-ptz-zoom-panel__row">
        <div
          ref={trackRef}
          className={[
            "ds-ptz-zoom-panel__spring",
            dragging ? "ds-ptz-zoom-panel__spring--dragging" : "",
            springActive ? "ds-ptz-zoom-panel__spring--active" : "",
            activeSide === "in" ? "ds-ptz-zoom-panel__spring--zoom-in" : "",
            activeSide === "out" ? "ds-ptz-zoom-panel__spring--zoom-out" : "",
            !ptzOperable ? "ds-ptz-zoom-panel__spring--disabled" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          role="slider"
          aria-label="Hold left to zoom out, right to zoom in"
          aria-valuemin={sliderMin}
          aria-valuemax={sliderMax}
          aria-valuenow={displayZoom}
          aria-valuetext={`${displayZoom}×`}
          tabIndex={ptzOperable && onZoomPressStart ? 0 : -1}
          onPointerDown={onTrackPointerDown}
        >
          <span className="ds-ptz-zoom-panel__spring-rail" aria-hidden />
          <span
            className={[
              "ds-ptz-zoom-panel__spring-label",
              "ds-ptz-zoom-panel__spring-label--minus",
              atLimit.zoomOut ? "ds-ptz-zoom-panel__spring-label--limit" : "",
            ].join(" ")}
          >
            −
          </span>
          <span
            className={[
              "ds-ptz-zoom-panel__spring-label",
              "ds-ptz-zoom-panel__spring-label--plus",
              atLimit.zoomIn ? "ds-ptz-zoom-panel__spring-label--limit" : "",
            ].join(" ")}
          >
            +
          </span>
          <span className="ds-ptz-zoom-panel__spring-center-mark" aria-hidden />
          <span
            className="ds-ptz-zoom-panel__spring-thumb"
            style={{ left: `${thumbPct}%` }}
            aria-hidden
          />
        </div>
        <button
          type="button"
          className="ds-ptz-zoom-panel__reset"
          disabled={!ptzOperable}
          title="Reset zoom to wide (1×)"
          aria-label="Reset zoom to wide"
          onClick={(e) => {
            e.stopPropagation();
            releaseSpring();
            onResetZoom();
          }}
        >
          Reset 1x
        </button>
      </div>
    </div>
  );
}
